import React, { useEffect, useRef, useState } from "react";

import CrossIcon from "theme/components/icons/CrossIcon";
import Div from "theme/layouts/Div";
import PropTypes from "prop-types";
import ThemeButton from "theme/components/buttons/ThemeButton";
import clear from "core-web/state/models/Search/dispatches/clear";
import hexWithOpacity from "core-web/utils/hexWithOpacity";
import search from "core-web/state/models/Search/dispatches/search";
import styleObjectToProps from "core-web/utils/styleObjectToProps";
import styled from "theme/libs/styled";
import useBelowBreakpoint from "core-web/hooks/useBelowBreakpoint";
import useDebounce from "core-web/hooks/useDebounce";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const SearchInputWrapper = styled("div")`
    position: relative;
    display: flex;
    gap: 8px;
    padding-right: 14px;
`;

const SearchTextInput = styled("input")`
    width: 100%;
    border: none;
    background-color: inherit;
    box-shadow: none;
    transition: all 0.1s;

    &:focus {
        box-shadow: none;
        outline: none;
        ${({ theme }) => theme.colors.textGrey};
    }

    &::-ms-clear {
        display: none;
    }

    &::-webkit-input-placeholder {
        color: ${({ theme }) => hexWithOpacity(theme.colors.black, 90)};
    }

    /* clears the 'X' from Chrome */
    ::-webkit-search-decoration,
    ::-webkit-search-cancel-button,
    ::-webkit-search-results-button,
    ::-webkit-search-results-decoration {
        display: none;
    }
`;

const SearchInput = ({ maxLength, isSearchOpen, closeSearch, onInputFocus, searchPath, ...rest }) => {
    const location = useLocation();
    const { t } = useTranslation();
    const isInitialMount = useRef(false);
    const inputRef = useRef();
    const history = useHistory();

    const inputPlaceholder = useSelector(({ header }) => header?.data?.search?.input_placeholder_text) || "";
    const reduxSearchState = useSelector(({ search }) => search?.query);
    const [query, setQuery] = useState(reduxSearchState);

    const textStyles = styleObjectToProps(["Myriad Pro/16-100", null, null, null, "Myriad Pro/14-100"]);

    const siteSearch = (query) => {
        search(query, history, location);

        if (query?.length < 1) {
            clear(history, location);
        }
    };

    const debouncedQuery = useDebounce(query, 350);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            siteSearch(debouncedQuery);
        }
    }, [debouncedQuery]);

    useEffect(() => {
        if (reduxSearchState !== debouncedQuery) {
            setQuery(reduxSearchState);
        }
    }, [reduxSearchState]);

    const handleKeyPress = (e) => {
        const term = e.target.value;

        if (e.keyCode === 13) {
            // Enter
            e.target.blur();
            const path = term.length ? `${searchPath}?q=${term}` : searchPath;
            history.push(path);

            closeSearch();
        }

        if (e.keyCode === 27) {
            // ESC
            e.target.blur();
            e.preventDefault();
            closeSearch();
        }
    };

    const handleOnFocus = () => {
        if (onInputFocus) {
            onInputFocus();
        }
    };

    const clearSearch = () => {
        siteSearch("");
        setQuery("");
        closeSearch();
    };

    const decodedQuery = (query) => {
        let decodedQuery = query;
        try {
            decodedQuery = decodeURI(decodedQuery);
        } catch (e) {
            console.error(e);
        }

        return decodedQuery;
    };

    const handleWrapperClick = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const isMobile = useBelowBreakpoint("tabletLg");

    return (
        <Div width="100%" onClick={handleWrapperClick} {...rest}>
            <SearchInputWrapper width={isMobile || isSearchOpen ? "auto" : "0"}>
                <label style={{ position: "absolute", left: "-10000px" }} htmlFor="searchField" name="searchField">
                    searchField
                </label>

                <SearchTextInput
                    id="searchField"
                    type="search"
                    ref={inputRef}
                    // Sets correct action label on virtual keyboards.
                    enterkeyhint="search"
                    placeholder={inputPlaceholder || t("search.placeholder")}
                    maxLength={maxLength}
                    autoComplete="off"
                    value={decodedQuery(query)}
                    onFocus={handleOnFocus}
                    onChange={(e) => setQuery(e.target.value.replace(/^\s+/, ""))}
                    onKeyDown={handleKeyPress}
                    {...textStyles}
                />

                {isSearchOpen && (
                    <ThemeButton
                        disableHover
                        onClick={clearSearch}
                        buttonTheme="transparent"
                        p="0"
                        aria-label={t("aria_labels.close_search")}
                    >
                        <CrossIcon width="12px" height="12px" />
                    </ThemeButton>
                )}
            </SearchInputWrapper>
        </Div>
    );
};

SearchInput.propTypes = {
    closeSearch: PropTypes.func,
    isSearchOpen: PropTypes.bool,
    maxLength: PropTypes.string,
    onInputFocus: PropTypes.func,
    query: PropTypes.string,
    searchPath: PropTypes.string,
};

SearchInput.defaultProps = {
    closeSearch: () => {},
    isSearchOpen: false,
    maxLength: "50",
    onInputFocus: () => {},
    query: "",
    searchPath: "",
};

export default SearchInput;
